import React, { useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleExclamation, faRotateRight } from '@fortawesome/pro-solid-svg-icons';

import { Popover } from '@buildoutinc/blueprint-react/ui/Popover';

import { Button } from 'components/ui/button';
import { put } from 'helpers/api';

interface ListingIndicator {
  listingType: string;
  message: string;
  retryable: boolean;
  retryPath: string;
  status: 'success' | 'failure';
  syncedAtLabel: string;
}

interface Props {
  listings: ListingIndicator[];
  status: 'success' | 'failure';
}

const LISTING_TYPE_LABELS: Record<string, string> = { lease: 'Lease', sale: 'Sale' };

// One listing type's row inside the Dash sync popover: status icon, message, timestamp, and a Retry
// button (failures only) that re-enqueues just this listing. Retry is async, so the button reports
// "Re-sync queued" and the icon updates on the next page load rather than flipping immediately.
export function ListingSection({ listing, showLabel }: { listing: ListingIndicator; showLabel: boolean }) {
  const [pending, setPending] = useState(false);
  const [queued, setQueued] = useState(false);
  const success = listing.status === 'success';

  const retry = async (): Promise<void> => {
    if (pending) return;

    setPending(true);
    try {
      await put(listing.retryPath, {});
      setQueued(true);
    } catch {
      // The sync is re-enqueued server-side; leave the section as-is if the request fails.
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-1">
      {showLabel && <div className="small fw-semibold">{LISTING_TYPE_LABELS[listing.listingType]}</div>}
      <div className="d-flex gap-2">
        <FontAwesomeIcon
          className={success ? 'text-success mt-1' : 'text-danger mt-1'}
          icon={success ? faCircleCheck : faCircleExclamation}
        />
        <div className="d-flex flex-column gap-1 flex-grow-1">
          <div className="small">{listing.message}</div>
          <div className="small text-muted">{listing.syncedAtLabel}</div>
          {listing.retryable && (
            <div className="d-flex justify-content-end">
              <Button
                disabled={pending || queued}
                icon={faRotateRight}
                label={queued ? 'Re-sync queued' : 'Retry'}
                size="sm"
                variant="ghost"
                onClick={retry}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Header chip + popover summarizing the AnywhereRE (Dash) sync per listing. The chip icon reflects
// the aggregate (red when any listing failed); the popover breaks it out by listing type.
export default function AnywhereReSyncStatus({ listings, status }: Props) {
  const success = status === 'success';
  const showLabels = listings.length > 1;

  return (
    <Popover>
      <Popover.Trigger
        render={(
          <Button variant="outline">
            DASH Sync
            <FontAwesomeIcon
              className={success ? 'text-success ms-2' : 'text-danger ms-2'}
              icon={success ? faCircleCheck : faCircleExclamation}
            />
          </Button>
        )}
      />
      <Popover.Content side="bottom">
        <Popover.Body className="d-flex flex-column gap-2">
          <div className="fw-semibold">DASH Sync</div>
          {listings.map((listing, index) => (
            <React.Fragment key={listing.listingType}>
              {index > 0 && <div className="border-top" />}
              <ListingSection listing={listing} showLabel={showLabels} />
            </React.Fragment>
          ))}
        </Popover.Body>
      </Popover.Content>
    </Popover>
  );
}
